import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { notificationKeys } from '@/services/queryKeys';
import { setNotificationSocketConnected } from '@/hooks/useNotifications';
import type { RootState } from '@/redux';

/**
 * Socket duy nhất đang giữ trạng thái "connected" — xem guard trong `markConnected`.
 * Module-scope (không phải ref) vì chỉ 1 instance hook được mount ở Layout.
 */
let activeSocket: Socket | null = null;

/**
 * Realtime notification subscription (EG-355 §4 acceptance criteria).
 *
 * Connect socket.io tới `<VITE_WS_URL>/notification`, join room
 * `tenant:<tenantId>`, lắng `notification.created` / `notification.read` →
 * invalidate query `notifications` để FE re-fetch list + unread count.
 *
 * Graceful degrade: nếu BE chưa có gateway hoặc socket fail → unread-count vẫn
 * poll dự phòng 60s (`useUnreadCount` với `socketConnected = false`) → không block UX.
 *
 * P4: hook này còn CÔNG BỐ trạng thái kết nối qua `setNotificationSocketConnected` để
 * `useUnreadCount` giãn interval 60s → 300s khi realtime đã chạy (mất kết nối thì tự quay
 * về 60s qua sự kiện `disconnect`/`connect_error`).
 *
 * Mount 1 lần ở Layout (sau khi authenticated). Không mount nhiều instance —
 * mỗi tab giữ 1 socket connection.
 */
export function useNotificationSocket(): void {
  const qc = useQueryClient();
  const tenantId = useSelector((s: RootState) => s.auth.user?.tenantId);
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);

  useEffect(() => {
    if (!tenantId || !accessToken) return;
    const wsUrl = import.meta.env.VITE_WS_URL;
    if (!wsUrl) return;

    const socket: Socket = io(`${wsUrl}/notification`, {
      transports: ['websocket'],
      auth: { token: accessToken, tenantId },
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });

    // Chỉ socket "đang hoạt động" được quyền ghi trạng thái: khi effect chạy lại (đổi token/tenant)
    // hoặc StrictMode mount lại, sự kiện disconnect của socket cũ có thể tới SAU khi socket mới đã
    // connect — không có guard này thì badge lại tưởng realtime đã tắt và poll về 60s.
    activeSocket = socket;

    const markConnected = (connected: boolean) => {
      if (activeSocket !== socket) return;
      setNotificationSocketConnected(connected);
    };

    socket.on('connect', () => {
      markConnected(true);
      socket.emit('subscribe_tenant', { tenantId });
    });

    const invalidateNotifications = () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    };

    socket.on('notification.created', invalidateNotifications);
    socket.on('notification.read', invalidateNotifications);
    socket.on('notification.updated', invalidateNotifications);

    socket.on('connect_error', (err) => {
      // Mất realtime → useUnreadCount quay lại poll 60s, và polling vẫn là fallback của list khi
      // người dùng mở dropdown.
      markConnected(false);
      // Log debug để dev biết WS chưa wire BE.
      if (import.meta.env.DEV) {
        console.debug('[useNotificationSocket] connect_error:', err.message);
      }
    });

    // Server/transport đóng kết nối (mất mạng, token hết hạn, gateway restart) → về poll 60s cho
    // tới khi reconnect thành công.
    socket.on('disconnect', () => markConnected(false));

    return () => {
      if (activeSocket === socket) activeSocket = null;
      setNotificationSocketConnected(false);
      socket.disconnect();
    };
  }, [tenantId, accessToken, qc]);
}
